import { createFileRoute, useNavigate } from "@tanstack/react-router";
import ReportBuilderWrapper from "@/components/reports/report-builder/report-builder-wrapper";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { useQuery, useMutation } from "convex/react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { FullscreenButton } from "@/components/ui/fullscreen-button";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { getTemplateById } from "@/components/reports/report-builder/config/templates";

const builderSearchSchema = z.object({
  template: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/reports/$id/builder")({
  validateSearch: builderSearchSchema,
  component: ReportBuilderPage,
});

function ReportBuilderLoading() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

function ReportBuilderPage() {
  const { id } = Route.useParams();
  const { template: templateId } = Route.useSearch();
  const navigate = useNavigate();
  const isNewReport = id === "new";
  const isCreatingRef = useRef(false);

  const createReport = useMutation(api.companies.reports.api.mutations.create);

  const report = useQuery(
    api.companies.reports.api.queries.get,
    isNewReport ? "skip" : { id: id as Id<"reports"> },
  );

  useEffect(() => {
    if (isNewReport && !isCreatingRef.current) {
      isCreatingRef.current = true;

      const createNewReport = async () => {
        try {
          const template = templateId ? getTemplateById(templateId) : null;

          const newReportId = await createReport({
            title: template?.metadata.title ?? "Sin titulo",
            description: template?.metadata.description ?? "",
            status: "active",
            visibility: template?.metadata.visibility ?? "private",
            format: template?.format ?? "pdf",
            dataSource: template?.dataSource ?? "sales",
            config: template?.config ?? {
              filters: [],
              columns: [],
              sorts: [],
              aggregations: [],
            },
            distribution: {
              email: { enabled: false, recipients: [] },
              notification: false,
              webhook: { enabled: false },
              storage: { enabled: true, path: "/reports" },
            },
          });

          navigate({
            to: "/reports/$id/builder",
            params: { id: newReportId },
            replace: true,
          });
        } catch (error) {
          console.error("Error creating report:", error);
        }
      };

      createNewReport();
    }
  }, [isNewReport, createReport, navigate, templateId]);

  if (isNewReport) {
    return <ReportBuilderLoading />;
  }

  if (report === undefined) {
    return <ReportBuilderLoading />;
  }

  if (report === null) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            No se pudo encontrar el reporte solicitado
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <ReportBuilderWrapper
        reportId={id as Id<"reports">}
        initialConfig={report.config}
        initialMetadata={{
          title: report.title,
          description: report.description,
          status: report.status,
          visibility: report.visibility,
          format: report.format,
          dataSource: report.dataSource,
          sharedUsers: report.sharedUsers || [],
        }}
        initialSchedule={report.schedule}
        initialDistribution={report.distribution}
      />
      <FullscreenButton />
    </>
  );
}
